import { getValidatedLimitParam, getValidatedPageParam, useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import { TParam, usePageParams } from 'hooks/use-page-params';
import { UseQueryOptions, UseQueryResult } from 'react-query';

const endpoint = 'catalog/v1/catalog-categories';
export const getProductsEndpointIdentifier = endpoint;

export type TData = Record<string, any>;
type TError = AxiosError;
type TQueryKey = [
  string,
  {
    page: TParam;
    limit: TParam;
    code: TParam;
    name: TParam;
    sort: TParam;
    status: TParam;
  }
];
type Options = Omit<UseQueryOptions<unknown, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>;
type ReturnType = UseQueryResult<TData, TError>;

export function useGetProductsApi(categoryId: number, options?: Options): ReturnType {
  const toast = useCustomToast();
  const isLoggedIn = useIsLoggedIn();
  const page = getValidatedPageParam();
  const limit = getValidatedLimitParam();
  const { getPageParams } = usePageParams();
  const fullEndpoint = `${endpoint}/${categoryId}/legacy-products`;

  const { code, name, sort, status } = getPageParams();

  const queryFn = async () => {
    try {
      const { data } = await http.get(fullEndpoint, {
        params: {
          page,
          limit,
          code,
          name,
          sort,
          status,
        },
      });

      return data;
    } catch (error: any) {
      if (!error.response.status || !error.response.data.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useQueryWrapper(
    [
      fullEndpoint,
      {
        page,
        limit,
        code,
        name,
        sort,
        status,
      },
    ],
    queryFn,
    {
      enabled: isLoggedIn,
      ...options,
    }
  );
}
